import React, {
    ChangeEvent,
    CSSProperties,
    FocusEvent,
    Fragment,
    InputHTMLAttributes,
    KeyboardEvent,
    MutableRefObject,
    SyntheticEvent
} from 'react';
import isEqual from 'lodash-es/isEqual';
import classNames from 'classnames';
import config from "../../config/config";
import Label from './Label';

const removableProps = [
    "select",
    "saveChanges",
    "nolabel",
    "formId",
    "formatter",
    "componentConfig",
    "negative",
    "disablePaste",
    "warning",
    "valid",
    "error",
    "suggestion"
];

export type TextInputPropsType = {
    role?: string|"idfield",
    onBlur?: (e: FocusEvent<HTMLInputElement>, field: string, value: string|number|null|undefined) => void,
    saveChanges?: (field: string, value: any, e: ChangeEvent) => void,
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>, field: string, value: string|number|null|undefined) => void,
    label?: any,
    focus?: boolean|string,
    autoFocus?: boolean|string,
    select?: boolean,
    autocomplete?: string|boolean,
    required?: boolean,
    disabled?: true|false,
    readOnly?: true|false,
    error?: any,
    warning?: any,
    valid?: any,
    value: string|number|null|undefined,
    placeholder?: string,
    name?: string,
    className?: string|Record<string, any>,
    disablePaste?: boolean,
    formatter?: (value: string|number|null|undefined) => React.ReactNode,
    suggestion?: (value: string|number|null|undefined) => string,
    style?: CSSProperties,
}&({
    type?: "number"|"decimal",
    negative?: boolean,
    min?: number,
    max?: number,
}|{
    type?: "string"|"text",
    pattern?: string,
})

type TextInputStateType = {
    value: string|number|null|undefined,
    focus: boolean,
    suggestion: string
}

class TextInput extends React.Component<TextInputPropsType, TextInputStateType> {

    state: TextInputStateType = {
        value: null,
        focus: false,
        suggestion: ''
    }
    
    input: MutableRefObject<HTMLInputElement>;

    suggestion: (() => void)&{abort?: () => void};

    constructor(props: TextInputPropsType) {
        super(props);
        this.input = React.createRef<HTMLInputElement>();
    }


    focus = () => {
        if (this.input.current) {
            this.input.current.focus();
        }
    }

    select = () => {
        if (this.input.current) {
            this.input.current.select && this.input.current.select();
            this.input.current.setSelectionRange && this.input.current.setSelectionRange(0, this.input.current.value.length);
        }
    }

    componentDidUpdate(prevProps: TextInputPropsType, prevState: TextInputStateType) {
        if (!this.props.onBlur && prevProps.onBlur) {
            this.setState({value: null});
        }
        const prevFocus = prevProps.focus || prevProps.autoFocus;
        const focus = this.props.focus || this.props.autoFocus;
        if (prevFocus !== focus && focus) {
            this.focus();
        }
        if (this.props.select) {
            this.select();
        }
        if (this.props.value !== prevProps.value) {
            if (!(Number.isNaN(this.props.value) && Number.isNaN(prevProps.value))) {
                this.setState({value: Number.isNaN(this.props.value) ? null : this.props.value, suggestion: ''});
            }
        }
    }

    componentDidMount() {
        if (this.props.focus) {
            this.focus();
        }
        if (this.props.select) {
            this.select();
        }
    }

    onFocus = () => this.setState({focus: true, value: Number.isNaN(this.props.value) ? null : this.props.value});

    isValid = (e: KeyboardEvent|FocusEvent|SyntheticEvent<HTMLInputElement>) => {
        // @ts-ignore
        const inputValue: string|number|null|undefined = e.target.value;
        if (this.props.type === "number" || this.props.type === "decimal") {
            if (this.props.negative && inputValue === "-") {
                return true;
            }
            let value = (inputValue + "").indexOf(".") !== -1 ? parseFloat(inputValue + "") : parseInt(inputValue + "");
            value = Number.isNaN(value) && inputValue === "" ? 0 : value;
            if (this.props.min !== undefined && this.props.max !== undefined) {
                return value >= this.props.min && value <= this.props.max;
            } else if (this.props.min !== undefined) {
                return value >= this.props.min;
            } else if (this.props.max !== undefined) {
                return value <= this.props.max;
            }
        }
        // @ts-ignore
        if (e.target.validity?.valid) return true;
        if (inputValue === '') return true;
        return false;
    }

    getReturnValue = (v: string|number|null|undefined) => {
        return (this.props.type === "number" || this.props.type === "decimal") && !v ? (v === "-" ? v : null) : v;
    }

    onBlur = (e: FocusEvent<HTMLInputElement>) => {
        const valid = this.isValid(e);
        this.setState({focus: false}, () => {
            if (valid && !isEqual(this.state.value, this.props.value)) {
                const v = this.state.value;
                this.setState({value: null}, () => {
                    if (this.props.onBlur) {
                        this.props.onBlur(e, this.props.name, this.getReturnValue(v));
                    }
                });
            }
        });
    }

    onChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (this.isValid(e)) {
            this.setState({value: e.target.value});
            if (this.props.saveChanges) {
                this.props.saveChanges(this.props.name, this.getReturnValue(e.target.value), e);
            }
        }
    }

    onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (this.isValid(e)) {
            // @ts-ignore
            const inputValue: string|number|null|undefined = e.target.value;
            this.setState({value: inputValue, suggestion: ''});
            if (this.props.onKeyDown) {
                this.props.onKeyDown(e, this.props.name, this.getReturnValue(inputValue));
            }
        }
    }

    onKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
        if (this.isValid(e)) {
            this.suggestion && this.suggestion.abort();
            // @ts-ignore
            const inputValue: string|number|null|undefined = e.target.value;
            if (
                e.which !== 8
                &&
                this.props.suggestion
                &&
                String(inputValue ?? '').length >= config.minimumLengthOfSearch
                &&
                // @ts-ignore
                e.target.selectionStart >= String(inputValue ?? '').length - 1
            ) {
                let value = inputValue;
                let aborted = false;
                let t: number;
                this.suggestion = async () => {
                    t = window.setTimeout(async () => {
                        let suggestion = await this.props.suggestion(value);
                        if (aborted) return;
                        this.setState({
                            suggestion
                        });
                    }, 300);
                }
                this.suggestion.abort = () => {
                    t && clearTimeout(t);
                    aborted = true;
                }
                this.suggestion();
            }
        }
    }

    filterProps = (props: TextInputPropsType): InputHTMLAttributes<HTMLInputElement> => {
        const filterProps: any = {...props};
        removableProps.forEach((field) => {
            delete filterProps[field];
        })
        if(props.focus !== undefined) {
            filterProps.focus = props.focus ? "true" : "false";
        }
        if (props.label === false) {
            filterProps.label = "";
        }
        return filterProps as InputHTMLAttributes<HTMLInputElement>;
    }

    render() {
        const props: InputHTMLAttributes<HTMLInputElement> = {};
        props.type = this.props.type || "text";
        if((this.props.type === "string" || this.props.type === "text") && this.props.pattern) {
            props.pattern = this.props.pattern;
        }
        // bug/hot fix https://stackoverflow.com/a/52592568/1149311
        /*if(type === "number") {
            type = "tel";
        }*/
        if (this.state.focus) {
            if (this.props.type === "number") {
                props.type = "text";
                props.pattern = (this.props.negative ? "[\-]?" : "") + "[0-9]*";
            }
            if (this.props.type === "decimal") {
                props.type = "text";
                props.pattern = (this.props.negative ? "[\-]?" : "") + "[0-9]{1,}[.]?[0-9]*"; // "[0-9]*[.]?[0-9]*"
            }
        } else {
            props.type = "text";
            props.pattern = undefined;
        }

        let value = this.state.value !== null && this.state.value !== undefined ? this.state.value : (this.props.value !== null && this.props.value !== undefined ? this.props.value : '');
        value = Number.isNaN(value) ? '' : value;

        let suggested = '';
        let formatted: any = value;
        // !(this.input.current && this.input.current === document.activeElement)
        if (!this.state.focus && this.props.formatter) {
            formatted = this.props.formatter(value);
            if (formatted instanceof Function) {
                formatted = formatted();
            } else if (formatted instanceof Object && ((formatted || {}).props || {})) {
                formatted = (formatted.props || {}).children || "";
            }
            if (Array.isArray(formatted)) {
                formatted = formatted.join("");
            }
        } else if (this.state.focus && this.state.suggestion && this.state.suggestion.indexOf(formatted) === 0) {

            // https://stackoverflow.com/a/37511463
            let fieldValue = String(this.state.suggestion);
            fieldValue = fieldValue.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            let value = String(formatted);
            value = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

            console.log(fieldValue, value, fieldValue.indexOf(value) === 0);
            if (fieldValue.indexOf(value) === 0) {
                suggested = formatted;
                formatted = this.state.suggestion;
            }
        }

        return <Fragment>
            <Label {...this.props} />
            <input
                ref={(el) => {
                    this.input.current = el;
                    if (this.input.current) {
                        if (suggested) {
                            this.input.current.setSelectionRange(suggested.length, formatted.length);
                        }
                        this.input.current.onpaste = e => {
                            if (this.props.disablePaste) {
                                e.preventDefault();
                                return false;
                            }
                        }
                    }
                }}
                {...this.filterProps(this.props)}
                {...props}
                autoComplete={(this.props.autocomplete === false ? "off" : (this.props.autocomplete === true ? undefined : this.props.autocomplete)) }
                value={formatted}
                name={this.props.name}
                placeholder={this.props.placeholder}
                className={classNames(
                    "p-inputtext p-component form-control",
                    {"is-invalid": this.props.error, "is-warned": this.props.warning, "is-valid": this.props.valid},
                    this.props.className
                )}
                style={this.props.style}
                onChange={this.onChange}
                onBlur={this.onBlur}
                onFocus={this.onFocus}
                onKeyDown={this.onKeyDown}
                onKeyUp={this.onKeyUp}
                disabled={!!(this.props.disabled || this.props.role === "idfield")}
                readOnly={this.props.readOnly}
            />
            {this.props.error && this.props.error !== true && <div className="invalid-feedback">{this.props.error}</div>}
            {this.props.warning && this.props.warning !== true && <div className="warning-feedback">{this.props.warning}</div>}
            {this.props.valid && this.props.valid !== true && <div className="valid-feedback">{this.props.valid}</div>}
        </Fragment>;
    }
}

export default TextInput;